import { create } from 'zustand';
import type { PublicUser } from '@sfco/shared';

/**
 * Auth/session state. Mirrors `connection.ts` shape — pure state, no IO.
 * Populated by `<AuthProvider>` on boot via `fetchMe()`, then mutated by
 * the login/signup/logout flows.
 *
 * `status` lifecycle:
 *   unknown → loading → (authenticated | unauthenticated)
 * Components that gate on auth use `status` rather than `user` so the
 * initial paint can show a splash instead of redirecting prematurely.
 */
export type AuthStatus = 'unknown' | 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: AuthStatus;
  user: PublicUser | null;
  setUser: (user: PublicUser | null) => void;
  setStatus: (status: AuthStatus) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,
  setUser: (user) =>
    set({
      user,
      status: user ? 'authenticated' : 'unauthenticated',
    }),
  setStatus: (status) => set({ status }),
  reset: () => set({ status: 'unauthenticated', user: null }),
}));
